import React, { useContext, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Chip, Grid, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import { ConfigContext } from '../../../../contextAPI/configContext';
import { selectProfileData, selectVersionKeys } from '../../../../redux/selectors/orderPlacementSelectors';
import useProfile from '@/components/_hooks/useProfile';
import OutputVersionOverflowMenu from './OutputVersionOverflowMenu';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { formatBytes } from '@/_utils/utils';

interface OutputVersionHeaderProps {
  files: InventoryFileEntity[];
  group: any;
  isReview: boolean;
  jobNumber: number;
  onEdit: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

interface FormValues {
  jobs: any[];
}

const OutputVersionHeader = ({ files, group, isReview, jobNumber, onEdit, onDuplicate, onRemove }: OutputVersionHeaderProps) => {
  const { values } = useFormikContext<FormValues>();
  const { outputVersionKey } = useProfile(values);
  const outputHeader = useContext(ConfigContext)?.orderPlacementConfig?.outputHeader;
  const totalFilesSizeWarningThreshold = useContext(ConfigContext)?.orderPlacementConfig?.totalFilesSizeWarningThreshold;
  const versionKeys = useAppSelector(selectVersionKeys);
  const profileData = values.jobs?.[0]?.profile?.data || useAppSelector(selectProfileData) || {};
  const outputVersion = outputVersionKey && versionKeys[group.title_inputs[outputVersionKey]];

  const totalFilesSize = useMemo(() => files.reduce((acc, cur) => acc + cur.file_size, 0), [files]);
  const showLargeFilesSizeWarning = totalFilesSizeWarningThreshold ? totalFilesSize > totalFilesSizeWarningThreshold : false;

  return (
    <Grid container flexWrap="nowrap" alignItems="center" sx={{ mx: 1 }}>
      <Grid item sx={{ mr: 'auto' }}>
        {outputHeader ? (
          // if we have a custom header set in the orderPlacement config, use that
          <>{outputHeader(group, outputVersion, profileData?.template_inputs?.edit_type) || <strong>Job {jobNumber}</strong>}</>
        ) : (
          // if custom header returns null, use the default header
          <strong>Job {jobNumber}</strong>
        )}
      </Grid>

      <Tooltip title={showLargeFilesSizeWarning ? 'The total size of selected files is too large. Please consider selecting fewer files.' : null}>
        <TotalFilesSize hasWarning={showLargeFilesSizeWarning}>
          {showLargeFilesSizeWarning && <FileSizeWarningIcon icon="error" />}
          {formatBytes(totalFilesSize)}
        </TotalFilesSize>
      </Tooltip>
      <Chip label={`${files?.length} file${files?.length !== 1 ? 's' : ''}`} sx={{ mr: 1, ml: 0.5 }} />
      {!isReview && <OutputVersionOverflowMenu files={files} group={group} onEdit={onEdit} onDuplicate={onDuplicate} onRemove={onRemove} />}
    </Grid>
  );
};

export default OutputVersionHeader;

const TotalFilesSize = styled.div<{ hasWarning: boolean }>`
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: bold;
  opacity: 0.7;
  margin: ${({ theme }) => theme.spacing('1px', 1.5, 0)};
  ${({ theme, hasWarning }) => (hasWarning ? `color: ${theme.palette.warning.main};` : '')}
`;

const FileSizeWarningIcon = styled(PolarisIcon)`
  font-size: 1.2rem;
  vertical-align: -0.3rem;
  margin-right: ${({ theme }) => theme.spacing(0.5)};
`;
